import {
  Button,
  Dialog,
  Grid,
  MenuItem,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import BackupIcon from '@material-ui/icons/Backup';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import LinkIcon from '@material-ui/icons/Link';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import NoteAddIcon from '@material-ui/icons/NoteAdd';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';
import PublishIcon from '@material-ui/icons/Publish';
import { cloneDeep, isEmpty } from 'lodash-es';
import { MTableHeader, MTableToolbar } from 'material-table';
import React, { ReactElement } from 'react';

// api
import {
  addGlobalVastToLineItems,
  createGlobalVast,
  deleteGlobalVast,
  getGlobalVasts,
  IAdserverGlobalVast,
  updateGlobalVast,
} from '~/src/dash/api/adserver-global-vast';
import { IAdserverCampaign } from '~/src/dash/api/campaign/campaign';
import { getCompanies, ICompany } from '~/src/dash/api/company';
import {
  addGlobalMediafilesToLineItems,
  createMediafileFromURL,
  deleteCreativesWithGlobalVastID,
  deleteGlobalMedia,
  deleteGlobalMediaToCreativeAssociation,
  getCampaigns,
  getGlobalMedia,
  IAdserverCreative,
  IAdserverGlobalMediafile,
  IVariantMediafile,
  updateGlobalMedia,
  updateVariantMediafile,
  uploadGlobalMediafile,
  uploadVastCSVfile,
} from '~/src/dash/api/console-api';
import { EBool } from '~/src/dash/api/types/bool';
import { CreateBannerModal } from '~/src/dash/components/create-banner-modal';
// components
import { CreateOrEditFromURLModal } from '~/src/dash/components/create-or-edit-from-url-modal';
import { CategoryMultiSelect } from '~/src/dash/components/dropdowns/category-multiselect';
import {
  IListTableColumn,
  IMediafileRowData,
  IVastRowData,
  ListTable,
  ListTableRowData,
} from '~/src/dash/components/list-table';
import { MenuButton } from '~/src/dash/components/menu-button';
import { ModalSelectTable } from '~/src/dash/components/modal-select-table';
import { ModelDialogDeleteView } from '~/src/dash/components/model-dialog-delete-view';
import { ModelMediumButtonStyle } from '~/src/dash/components/model-medium-button';
import ModelMissingPermission from '~/src/dash/components/model-missing-permission';
import { ModelSnackBarProgressBarContainer } from '~/src/dash/components/model-snackbar-progresbar-container';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { ITab, SettingsTabs } from '~/src/dash/components/settings-tabs';
import { Col, Cols, Panel, Panels, spacing } from '~/src/dash/publicaUI';
// other
import { IStore } from '~/src/dash/reducers/store';
import { getColorTheme, getContactEmail } from '~/src/dash/resellers/utils/importResellerConfig';
import { isPBSDomain } from '~/src/dash/resellers/utils/utils';
import {
  extractRouteParams,
  genDownloadVastCSVURL,
  navigate,
  RouteCampaignExisting,
} from '~/src/dash/router/routes';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import {
  CreateBannerFromAssetURLRow,
  CreateBannerFromDirectUploadRow,
  hasPrimaryAndSecondaryBanner,
  isPrimaryBannerForLineItemWithSecondaryBanner,
  isValidBannerExtension,
  isValidBannerMimetype,
  isValidBannerURL,
  isValidVideoExtension,
  isValidVideoMimetype,
  isValidVideoURL,
  shouldRejectBannerUpload,
} from '~/src/dash/utils/banner-ads-helpers';
import { publicaColors } from '~/src/dash/utils/colors';
// utils
import { parseDate } from '~/src/dash/utils/date';
import { decorate } from '~/src/dash/utils/decorator';
import { IPermissionCheck, IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { checkUserInfoMasked, hasRoleSuperUser } from '~/src/dash/utils/roles';
import { routeName } from '~/src/dash/utils/routes';
import UploadManager, { EUploadResponseType, IUploadStatus } from '~/src/dash/utils/upload_manager';
// views
import { ResolutionTypes } from '~/src/dash/views/campaign/campaign-form-view';
import { LoadingView } from '~/src/dash/views/loading-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

export interface IAdserverCampaignWithSelector extends IAdserverCampaign {
  tableData: { [checked: string]: boolean };
}

export interface IAdserverCreativeWithCampaignInfoAndSelector extends IAdserverCreative {
  LineItemID: string;
  LineItemName: string;
  AdserverOrderID: string;
  tableData: { [checked: string]: boolean };
}

export interface IInventoryRouteInfo {
  ID?: number;
  Name: string;
  VastURL?: string;
  DisableTooltip: boolean;
}

// leeway of +/- 0.03, translates w and h to popular ratios
const calculateAspectRatio = (width: number, height: number): string => {
  const ratio = width / height;
  if (ratio >= 1.75 && ratio <= 1.81) {
    return '16:9';
  } else if (ratio >= 0.53 && ratio <= 0.59) {
    return '9:16';
  } else if (ratio >= 0.63 && ratio <= 0.69) {
    return '2:3';
  } else if (ratio >= 0.77 && ratio <= 0.83) {
    return '4:5';
  } else if (ratio >= 0.97 && ratio <= 1.03) {
    return '1:1';
  } else if (ratio >= 1.3 && ratio <= 1.36) {
    return '4:3';
  } else if (ratio >= 1.57 && ratio <= 1.63) {
    return '16:10';
  } else if (ratio >= 1.97 && ratio <= 2.03) {
    return '2:1';
  } else if (ratio >= 2.32 && ratio <= 2.38) {
    return '47:20';
  } else {
    return 'Other';
  }
};

// converts a duration in seconds to a string
const convertDurationToString = (duration: number): string => {
  if (duration / 60 > 0) {
    return `${Math.floor(duration / 60)}m ${duration % 60}s`;
  } else {
    return `${duration}s`;
  }
};

// custom column for  global media file to line items modal
const globalMediafileToCreativesModalColumn = {
  title: 'Current Mediafiles',
  field: 'AllGlobalMediafiles',
  render: (rowData: IAdserverCampaignWithSelector) => {
    const allGlobalMediafiles = [] as IAdserverGlobalMediafile[];
    for (const creative of rowData.AdserverCreatives) {
      for (const globalMediafile of creative.AdserverGlobalMediafiles || []) {
        allGlobalMediafiles.push(globalMediafile);
      }
    }
    let menuItems = [
      <MenuItem key={rowData.ID} value={rowData.Name}>
        No Global Mediafiles
      </MenuItem>,
    ];
    if (allGlobalMediafiles.length) {
      menuItems = allGlobalMediafiles.map((mediafile: IAdserverGlobalMediafile) => {
        return (
          <MenuItem key={mediafile.ID} value={mediafile.Name}>
            {mediafile.Name}
          </MenuItem>
        );
      });
    }
    return (
      <TextField
        fullWidth
        select={true}
        label={`${allGlobalMediafiles.length} Mediafiles`}
        margin='normal'
        style={{ width: '200px', marginBottom: 10 }}
      >
        {menuItems}
      </TextField>
    );
  },
};

interface IState {
  // list resource
  mediafiles: IAdserverGlobalMediafile[];
  // list resource
  vasts: IAdserverGlobalVast[];
  // all line items for pub
  lineItems: IAdserverCampaign[];
  // line items that are using mediafiles for the modal (all line items with "checked" for modal)
  mediafileLineItems: IAdserverCampaignWithSelector[];
  // line items that are using vasts for the modal (all line items with "checked" for modal)
  vastLineItems: IAdserverCampaignWithSelector[];
  selectedID: string;
  selectedName: string;
  // list table data
  data: IMediafileRowData[];
  dataVast: { [key: number]: IVastRowData };
  loading: boolean;
  updating: boolean;
  // permissions
  readAuthorization: IPermissionCheck;
  writeAuthorization: IPermissionCheck;
  // Snackbar messages
  snackbarMessage: string;
  snackbarError: boolean;
  openCreativesDialog: boolean;
  // edit name temp id and name
  editableName: string;
  editableURL: string;
  editableID: string;
  // add creative modal
  openAddCreativeDialog: boolean;
  // when deleting a mediafile to creative association, these are saved for the modal
  selectedCreativeIDs: string;
  selectedCreativeNames: string;
  openDeleteAssoModelDialog: boolean;
  openVariantsDialog: boolean;
  // creatives selected for the modal directly in the list table
  selectedCreatives: IAdserverCreativeWithCampaignInfoAndSelector[];
  // create mediafile from url modal
  openCreateDialog: boolean;
  newCreateURL: string;
  newCreateName: string;
  // for creating new mediafile transcoding options
  selectedResolutions: string[];
  // force the menu button open/closed
  forceMenuButtonClosed: boolean;
  // name of current tab (used for modals)
  currentTab: string;
  // indicates if a vast is being edited rather than created when opening the create or edit modal
  edit: boolean;
  AdvertiserID: number;
  companies: ICompany[];
  openCreateBannerDialog: boolean;
  newCreateBannerURL: string;
  newCreateBannerName: string;
  BannerAdvertiserID: number;
}

export const CreativeLibraryListView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: () => ({
      button: ModelMediumButtonStyle,
      toolbarRow: {
        marginLeft: -10,
        width: '100%',
        marginTop: 5,
      },
      switch: {
        display: 'inline-flex',
        alignItems: 'center',
      },
      menuButtonText: {
        marginBottom: 2,
        paddingRight: 8,
        fontSize: '0.875rem',
        fontWeight: 500,
        color: '$434343',
      },
      menuButtonTextBlue: {
        marginBottom: 2,
        paddingRight: 8,
        fontSize: '0.875rem',
        fontWeight: 500,
        color: '#4952FF',
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      mediafiles: [] as IAdserverGlobalMediafile[],
      vasts: [] as IAdserverGlobalVast[],
      lineItems: [] as IAdserverCampaign[],
      mediafileLineItems: [] as IAdserverCampaignWithSelector[],
      vastLineItems: [] as IAdserverCampaignWithSelector[],
      selectedID: '',
      selectedName: '',
      data: [] as IMediafileRowData[],
      dataVast: {} as { [key: number]: IVastRowData },
      loading: true,
      updating: false,
      readAuthorization: IS_AUTHORIZED(['read_campaign'], this.props.user.user),
      writeAuthorization: IS_AUTHORIZED(['write_campaign'], this.props.user.user),
      snackbarMessage: '',
      snackbarError: false,
      openCreativesDialog: false,
      editableID: '',
      editableName: '',
      editableURL: '',
      openAddCreativeDialog: false,
      openDeleteAssoModelDialog: false,
      selectedCreativeIDs: '',
      selectedCreativeNames: '',
      selectedCreatives: [] as IAdserverCreativeWithCampaignInfoAndSelector[],
      openVariantsDialog: false,
      openCreateDialog: false,
      newCreateURL: '',
      newCreateName: '',
      selectedResolutions: ['1080p', '720p'],
      forceMenuButtonClosed: false,
      currentTab: 'Asset',
      edit: false,
      AdvertiserID: 0,
      companies: [] as ICompany[],
      openCreateBannerDialog: false,
      newCreateBannerURL: '',
      newCreateBannerName: '',
      BannerAdvertiserID: 0,
    };
    isNativeVastAuthorized =
      this.props.session.publisher.NativeVastEnabled === EBool.EBool_TRUE ||
      hasRoleSuperUser(this.props.user.user);

    uploadManager = new UploadManager();
    uploadKeyToIdx = new Map<string, { idx: number }>();

    // NOTE: We should try to build helper functions for componentDidMount, to keep the process clean
    // and readable, especially when we add more steps
    componentDidMount() {
      // add listener for uploader
      this.uploadManager.events.addListener(
        'uploadManager_uploadStatusChange',
        (item: IUploadStatus) => {
          if (item.status === EUploadResponseType.ABORTED && this.uploadKeyToIdx.has(item.key)) {
            const itemLocation = this.uploadKeyToIdx.get(item.key);
            if (itemLocation === undefined) {
              return;
            }
          }
        },
      );
      this.getGlobalMediaFilesAndLineItemsAndVasts();
    }

    componentWillUnmount() {
      this.uploadManager.cleanup();
    }

    // NOTE: In general, like we do with props and state, we should try to organize the functions
    // within a view/component to keep the code clean.  In addition, we should try to name the functions
    // as well as possible to show what is happening and if more explanation is needed, use a comment
    // above the function definition

    isCurrentTabAsset = (): boolean => {
      return this.state.currentTab === 'Asset';
    };

    // generates the data for the list table
    setTableData = () => {
      // create mediafile data
      const data: IMediafileRowData[] = this.state.mediafiles.map(
        (mediafile: IAdserverGlobalMediafile) => {
          let Bitrate = mediafile.Bitrate.toString();
          const bitRateNum = parseInt(mediafile.Bitrate, 10) || 0;
          if (bitRateNum > 10000) {
            Bitrate = Math.floor(bitRateNum / 1000) + ' kbps';
          }
          const advertiserForMediafile = this.state.companies.filter(
            (company) => company.ID === mediafile.AdvertiserID,
          )[0];
          return {
            ID: mediafile.ID,
            AdvertiserID: mediafile.AdvertiserID,
            Active: true,
            Name: checkUserInfoMasked(this.props.user.user)
              ? `demo_mediafile_${mediafile.ID}`
              : mediafile.Name,
            Duration: convertDurationToString(mediafile.Duration),
            Bitrate,
            AspectRatio: calculateAspectRatio(mediafile.Width, mediafile.Height),
            Dimensions: mediafile.Width.toString() + ' x ' + mediafile.Height.toString(),
            Creatives: mediafile.CreativeIDS.length,
            Variants: mediafile.VariantMediafiles.length,
            // this is specially defined from material-table, how they pass checked for selection
            tableData: { checked: false },
            EnableEditable: false,
            DisableDelete: mediafile.CampaignIDS.length !== 0,
            URL: mediafile.URL,
            Advertiser: advertiserForMediafile
              ? `[${mediafile.AdvertiserID}] ${advertiserForMediafile.Name}`
              : '',
            MimeType: mediafile.Type,
          };
        },
      );

      const dataVast: { [key: number]: IVastRowData } = {};
      this.state.vasts.forEach((vast: IAdserverGlobalVast) => {
        const advertiserForVAST = this.state.companies.filter(
          (company) => company.ID === vast.AdvertiserID,
        )[0];
        dataVast[vast.ID] = {
          ID: vast.ID.toString(),
          Name: checkUserInfoMasked(this.props.user.user) ? `demo_vast_${vast.ID}` : vast.Name,
          URL: checkUserInfoMasked(this.props.user.user) ? `demo_vast_url_${vast.ID}` : vast.URL,
          UpdatedAt: parseDate(vast.UpdatedAt),
          AdserverCreatives: vast.AdserverCreatives.length,
          Advertiser: advertiserForVAST
            ? `[${advertiserForVAST.ID}] ${advertiserForVAST.Name}`
            : '',
          AdvertiserID: advertiserForVAST ? advertiserForVAST.ID : 0,
          tableData: { checked: false },
          EnableEditable: true,
          EnableLink: true,
          DisableDelete: vast.CampaignIDS && vast.CampaignIDS.length !== 0,
        };
      });
      this.setState({
        data,
        dataVast,
      });
    };

    compareUpdatedAtDates = (rowData1: IVastRowData, rowData2: IVastRowData) => {
      return rowData1.UpdatedAt.isBefore(rowData2.UpdatedAt) ? -1 : 1;
    };

    // get global mediafiles and line items via api call and set state
    getGlobalMediaFilesAndLineItemsAndVasts(): void {
      this.setState(
        {
          loading: true,
        },
        () => {
          const publisherID = this.props.session.publisher.ID;
          Promise.all([
            getGlobalMedia({
              query: {
                publisher_id: publisherID,
              },
            }),
            getCampaigns({
              query: {
                publisher_id: publisherID,
              },
            }),
            getGlobalVasts(publisherID),
            getCompanies(publisherID),
          ])
            .then((resp) => {
              // all creatives for a single line item are either mediafile or vast type
              // we filter out line items using vast creatives
              // in addition, we want to add a selector for the modal
              const mediafileLineItems = [] as IAdserverCampaignWithSelector[];
              const vastLineItems = [] as IAdserverCampaignWithSelector[];
              for (const lineItem of resp[1]) {
                if (lineItem.AdserverCreatives.length !== 0) {
                  let isVast = false;
                  for (const creative of lineItem.AdserverCreatives) {
                    if (creative.AdserverGlobalVastID) {
                      isVast = true;
                      break;
                    }
                  }
                  const lineItemWithSelector = Object.assign(
                    {
                      tableData: { checked: false },
                    },
                    lineItem,
                  ) as IAdserverCampaignWithSelector;
                  if (isVast) {
                    vastLineItems.push(lineItemWithSelector);
                  } else {
                    mediafileLineItems.push(lineItemWithSelector);
                  }
                } else {
                  // if no creatives are available on this campaign, we can add mediafiles or vast
                  const lineItemWithSelector = Object.assign(
                    {
                      tableData: { checked: false },
                    },
                    lineItem,
                  ) as IAdserverCampaignWithSelector;
                  mediafileLineItems.push(lineItemWithSelector);
                  vastLineItems.push(lineItemWithSelector);
                }
              }
              this.setState(
                {
                  mediafileLineItems,
                  vastLineItems,
                  mediafiles: resp[0],
                  lineItems: resp[1],
                  vasts: resp[2].parsedBody || [],
                  companies: resp[3],
                  loading: false,
                },
                this.setTableData,
              );
            })
            .catch(() => {
              this.setState({
                snackbarError: true,
                snackbarMessage: 'Error loading creatives library. Please try again.',
                loading: false,
              });
            });
        },
      );
    }

    // ----- COMPONENT HELPER  FUNCTIONS ----- //
    handleCloseSnackbar = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    // handler helper functions
    handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      if (target.name === 'newCreateName') {
        this.setState({
          newCreateName: target.value,
        });
      } else if (target.name === 'newCreateURL') {
        this.setState({
          newCreateURL: target.value,
        });
      }
    };

    // handler helper functions
    handleChangeBanner = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      if (target.name === 'newCreateBannerName') {
        this.setState({
          newCreateBannerName: target.value,
        });
      } else if (target.name === 'newCreateBannerURL') {
        this.setState({
          newCreateBannerURL: target.value,
        });
      }
    };

    // First ID is taken since the dropdowns, while normally multi-select, are set to single
    handleChangeAdvertiser = (advertiserIDs: number[]) => {
      if (advertiserIDs.length) {
        this.setState({
          AdvertiserID: advertiserIDs[0],
        });
      } else {
        this.setState({
          AdvertiserID: 0,
        });
      }
    };

    handleCreateFromURL = () => {
      const { newCreateName, newCreateURL, AdvertiserID } = this.state;
      if (this.state.updating) {
        return;
      }
      if (newCreateName.length === 0) {
        this.setState({
          snackbarError: true,
          snackbarMessage: this.isCurrentTabAsset()
            ? 'Asset must have a name.'
            : 'VAST must have a name.',
        });
        return;
      } else if (newCreateURL.length === 0) {
        this.setState({
          snackbarError: true,
          snackbarMessage: this.isCurrentTabAsset()
            ? 'Asset must have a URL.'
            : 'VAST must have a URL.',
        });
        return;
      } else if (!this.isCurrentTabAsset() && isPBSDomain(newCreateURL)) {
        this.setState({
          snackbarError: true,
          snackbarMessage: `VAST cannot target a domain with that root.`,
        });
        return;
      }
      if (this.isCurrentTabAsset()) {
        // MEDIAFILE
        if (!isValidBannerURL(newCreateURL) && !isValidVideoURL(newCreateURL)) {
          this.setState({
            snackbarError: true,
            snackbarMessage: 'URL must end with supported file extension.',
          });
          return;
        }
        this.setState(
          {
            updating: true,
          },
          () => {
            createMediafileFromURL({
              query: {
                publisher_id: this.props.session.publisher.ID,
                name: newCreateName,
                url: newCreateURL,
                advertiser_id: AdvertiserID,
              },
            })
              .then(() => {
                this.getGlobalMediaFilesAndLineItemsAndVasts();
                this.setState({
                  updating: false,
                  openCreateBannerDialog: false,
                  snackbarMessage: 'Successfully created asset from URL',
                });
              })
              .catch((resp) => {
                this.setState({
                  updating: false,
                  openCreateBannerDialog: false,
                  snackbarMessage: `Failed to create asset from URL: ${resp}`,
                  snackbarError: true,
                });
              });
          },
        );
      } else {
        // VAST
        this.setState(
          {
            updating: true,
          },
          () => {
            const vast = {
              ID: 0,
              UpdatedAt: '',
              PublisherID: parseInt(this.props.session.publisher.ID, 10),
              URL: newCreateURL,
              Name: newCreateName,
              AdserverCreatives: [],
              CampaignIDS: [],
              AdvertiserID,
            } as IAdserverGlobalVast;
            createGlobalVast(vast, this.props.session.publisher.ID)
              .then(() => {
                this.getGlobalMediaFilesAndLineItemsAndVasts();
                this.setState({
                  updating: false,
                  openCreateDialog: false,
                  snackbarMessage: 'Successfully created VAST from URL',
                });
              })
              .catch((resp) => {
                this.setState({
                  updating: false,
                  openCreateDialog: false,
                  snackbarMessage: `Failed to create VAST from URL: ${resp}`,
                  snackbarError: true,
                });
              });
          },
        );
      }
    };

    handleCreateBannerFromURL = () => {
      const { newCreateBannerName, newCreateBannerURL, BannerAdvertiserID } = this.state;
      const uploadValidationResult = shouldRejectBannerUpload(
        newCreateBannerName,
        newCreateBannerURL,
      );
      if (!uploadValidationResult.rejected) {
        this.setState(
          {
            updating: true,
          },
          () => {
            createMediafileFromURL({
              query: {
                publisher_id: this.props.session.publisher.ID,
                name: newCreateBannerName,
                url: newCreateBannerURL,
                advertiser_id: BannerAdvertiserID,
              },
            })
              .then(() => {
                this.getGlobalMediaFilesAndLineItemsAndVasts();
                this.setState({
                  updating: false,
                  openCreateDialog: false,
                  snackbarMessage: 'Successfully created banner asset from URL',
                });
              })
              .catch((resp) => {
                this.setState({
                  updating: false,
                  openCreateDialog: false,
                  snackbarMessage: `Failed to create banner asset from URL: ${resp}`,
                  snackbarError: true,
                });
              });
          },
        );
      } else {
        this.setState({
          snackbarError: true,
          snackbarMessage: uploadValidationResult.reason,
        });
      }
    };

    handleUpdateFromURL = () => {
      const { newCreateName, newCreateURL, selectedID, AdvertiserID } = this.state;
      if (this.state.updating) {
        return;
      }
      if (newCreateName.length === 0) {
        this.setState({
          snackbarError: true,
          snackbarMessage: 'VAST must have a name.',
        });
        return;
      } else if (newCreateURL.length === 0) {
        this.setState({
          snackbarError: true,
          snackbarMessage: 'VAST must have a URL.',
        });
        return;
      } else if (!this.isCurrentTabAsset() && isPBSDomain(newCreateURL)) {
        this.setState({
          snackbarError: true,
          snackbarMessage: `VAST cannot target a domain with that root.`,
        });
        return;
      }

      if (this.isCurrentTabAsset()) {
        // MEDIAFILE
        if (!isValidBannerURL(newCreateURL) && !isValidVideoURL(newCreateURL)) {
          this.setState({
            snackbarError: true,
            snackbarMessage: 'URL must point to a .mp4 mediafile.',
          });
          return;
        }
        this.setState(
          {
            updating: true,
          },
          () => {
            updateGlobalMedia({
              query: {
                global_mediafile_id: parseInt(selectedID, 10),
                publisher_id: parseInt(this.props.session.publisher.ID, 10),
                name: newCreateName,
                advertiser_id: AdvertiserID,
              },
            })
              .then(() => {
                this.getGlobalMediaFilesAndLineItemsAndVasts();
                this.setState({
                  updating: false,
                  openCreateDialog: false,
                  snackbarMessage: 'Successfully updated asset from URL',
                });
              })
              .catch((resp) => {
                this.setState({
                  updating: false,
                  openCreateDialog: false,
                  snackbarMessage: `Failed to updated asset from URL: ${resp}`,
                  snackbarError: true,
                });
              });
          },
        );
      } else {
        // VAST
        this.setState(
          {
            updating: true,
          },
          () => {
            const vast = {
              ID: parseInt(selectedID, 10),
              UpdatedAt: '',
              PublisherID: parseInt(this.props.session.publisher.ID, 10),
              URL: newCreateURL,
              Name: newCreateName,
              AdserverCreatives: [],
              CampaignIDS: [],
              AdvertiserID,
            } as IAdserverGlobalVast;
            updateGlobalVast(vast, this.props.session.publisher.ID)
              .then(() => {
                this.getGlobalMediaFilesAndLineItemsAndVasts();
                this.setState({
                  updating: false,
                  openCreateDialog: false,
                  snackbarMessage: 'Successfully saved VAST from URL',
                });
              })
              .catch((resp) => {
                this.setState({
                  updating: false,
                  openCreateDialog: false,
                  snackbarMessage: `Failed to update VAST from URL: ${resp}`,
                  snackbarError: true,
                });
              });
          },
        );
      }
    };

    // checks if the files are valid, gives information to the uploadManager to show progress uploading then uploads the file
    // if there are multiple files, we want them to be uploaded sequentially and not in parallel
    handleUploadMediafiles = (
      { target }: React.ChangeEvent<HTMLInputElement>,
      isBanner?: boolean,
    ) => {
      this.setState({
        forceMenuButtonClosed: true,
      });
      if (target.files === null) {
        return;
      }
      const names = [] as string[];
      const files = [] as any[];

      // do a quick check to make sure all the files are valid
      if (target.files && target.files.length > 0) {
        for (let i = 0; i < target.files.length; i++) {
          const file = target.files[i];
          names.push(file.name);
          files.push(file);

          const filenameParams = file.name.split('.');
          const ext = filenameParams[filenameParams.length - 1].toLowerCase();
          if (isBanner === true) {
            if (!isValidBannerExtension(ext)) {
              this.setState({
                snackbarMessage: 'Only .gif, .jpg, and .png formats are supported.',
                snackbarError: true,
              });
              return;
            }
          } else if (!isValidVideoExtension(ext.toLowerCase())) {
            this.setState({
              snackbarMessage:
                'Currently the only supported mediafile format is .mp4 and .mov. If another mediafile is required, please send your request to ' +
                getContactEmail() +
                '.',
              snackbarError: true,
            });
            return;
          }
        }
      }

      // add the filenames and get keys
      const uploadKeys = this.uploadManager.addUploads(names);
      for (let i = 0; i < uploadKeys.length; i++) {
        const key = uploadKeys[i];
        this.uploadKeyToIdx.set(key, { idx: i });
      }

      this.uploadMediafile(files, names, uploadKeys, isBanner === true);
    };

    // upload mediafiles will call itself recursively after each upload is successfully completed
    // if there are multiple files, we want them to be uploaded sequentially and not in parallel
    uploadMediafile = (files: any[], names: string[], uploadKeys: string[], isBanner?: boolean) => {
      if (isEmpty(files) || isEmpty(names) || isEmpty(uploadKeys)) {
        // on the last recusive call, we can update mediafiles, vasts and line items
        this.getGlobalMediaFilesAndLineItemsAndVasts();
        return;
      }

      const name = names.shift();
      const file = files.shift();
      const uploadKey = uploadKeys.shift();
      if (name && file && uploadKey) {
        uploadGlobalMediafile({
          resolutions: this.state.selectedResolutions.join(','),
          publisher_id: this.props.session.publisher.ID,
          global_mediafile: 'true',
          is_banner: isBanner ? 'true' : 'false',
          name,
          file,
          managerFunc: (xhr: XMLHttpRequest) => {
            this.uploadManager.addListenersToUpload(uploadKey, xhr);
          },
        })
          .then((json: any) => {
            // upload the file, we give a timeout to make sure the uploadManager and event listeners are done talking
            // if any of the upload failed, signal failure
            if (json.status !== 200 && uploadKey) {
              this.uploadManager.signalFailure(uploadKey, json.response.error);
            } else if (uploadKey) {
              // signal success
              this.uploadManager.signalSuccess(uploadKey);
            }
            // since upload was success, call this function again with
            this.uploadMediafile(files, names, uploadKeys);
          })
          .catch(() => {
            // if fail, signal failure for this upload and upload next file
            if (uploadKey) {
              this.uploadManager.signalFailure(uploadKey, '');
            }
            this.uploadMediafile(files, names, uploadKeys);
          });
      }
    };

    // ----- HANDLER HELPER FUNCTIONS FOR LIST TABLE ----- //
    handleSetUpdating = (updating: boolean) => {
      this.setState({
        updating,
      });
    };

    // deletes the global mediafile or vast and gives user msg if success/failure, returns a promise
    // for ModelDialogDeleteView which requires a promise to close dialog
    // globalMediafiles currently in use by line items cannot be deleted
    // vasts will be deleted with the creatives they are attached to since a creative can only have 1 vast
    handleDeleteItem = (id: string) => {
      if (this.isCurrentTabAsset()) {
        // MEDIAFILE
        const mediafile = this.state.mediafiles.find(
          (mf: IAdserverGlobalMediafile) => mf.ID === id,
        );
        if (!mediafile) {
          return new Promise((resolve) => resolve(undefined));
        }

        if (mediafile.CampaignIDS.length !== 0) {
          this.setState({
            snackbarMessage: 'Mediafile currently in use by line items cannot be deleted.',
            snackbarError: true,
          });
          return new Promise((resolve) => resolve(undefined));
        }

        this.setState(
          {
            updating: true,
          },
          () => {
            deleteGlobalMedia({
              query: {
                publisher_id: this.props.session.publisher.ID,
                global_mediafile_id: id,
              },
            })
              .then(() => {
                this.setState(
                  {
                    snackbarMessage: 'Successfully deleted global media file.',
                    snackbarError: false,
                    updating: false,
                  },
                  this.getGlobalMediaFilesAndLineItemsAndVasts,
                );
              })
              .catch((err) => {
                console.error(err);
                this.setState({
                  snackbarMessage: 'Error while deleting global media file',
                  snackbarError: true,
                  updating: false,
                });
              });
          },
        );
      } else {
        // VAST
        const vast = this.state.vasts.find(
          (vast: IAdserverGlobalVast) => vast.ID.toString() === id,
        );
        if (!vast) {
          return new Promise((resolve) => resolve(undefined));
        }
        if (vast.CampaignIDS && vast.CampaignIDS.length !== 0) {
          this.setState({
            snackbarMessage: 'VAST currently in use by line items cannot be deleted.',
            snackbarError: true,
          });
          return new Promise((resolve) => resolve(undefined));
        }
        this.setState(
          {
            updating: true,
          },
          () => {
            deleteGlobalVast(vast.ID.toString(), this.props.session.publisher.ID)
              .then(() => {
                this.setState(
                  {
                    snackbarMessage: 'Successfully deleted VAST',
                    snackbarError: false,
                    updating: false,
                  },
                  this.getGlobalMediaFilesAndLineItemsAndVasts,
                );
              })
              .catch(() => {
                this.setState({
                  snackbarMessage: 'Error while deleting VAST',
                  snackbarError: true,
                  updating: false,
                });
              });
          },
        );
      }
      // the delete modal expects a promise to be returned
      return new Promise((resolve) => resolve(undefined));
    };

    // handles selection of a row in the list table
    // currently this handles a single select since material-table doesn't have a single select option
    handleSelectionChange = (_rows: ListTableRowData[], selectedRow: ListTableRowData) => {
      if (!selectedRow) {
        return;
      }
      const selectedID = selectedRow.ID;
      if (this.isCurrentTabAsset()) {
        const data = cloneDeep(this.state.data);
        for (let i = 0; i < data.length; i++) {
          if (selectedID === data[i].ID) {
            data[i].tableData.checked = !data[i].tableData.checked;
          } else {
            data[i].tableData.checked = false;
          }
        }
        this.setState({
          data: data as IMediafileRowData[],
        });
      } else {
        const data = cloneDeep(this.state.dataVast);
        for (const id in data) {
          if (selectedID === id) {
            data[id].tableData.checked = !data[id].tableData.checked;
          } else {
            data[id].tableData.checked = false;
          }
        }
        this.setState({
          dataVast: data,
        });
      }
    };

    // ----- HANDLER HELPER FUNCTIONS FOR MODALS ----- //

    handleCloseAddCreativeDialog = () => {
      this.setState({
        openAddCreativeDialog: false,
      });
    };

    handleCloseVariantDialog = () => {
      this.setState({
        openVariantsDialog: false,
      });
    };

    handleCloseCreativeDialog = () => {
      this.setState({
        openCreativesDialog: false,
      });
    };

    handleCloseCreateMediafileModal = () => {
      this.setState({
        openCreateDialog: false,
      });
    };

    // for add mediafiles via new creative to selected line items
    // adds "selected" to the mediafileLineItems in the modal
    handleSelectionChangeAddLineItems = (rows: IAdserverCampaignWithSelector[]) => {
      const mediafileLineItems = cloneDeep(this.state.mediafileLineItems);
      for (const row of rows) {
        const rowID = row.ID.toString();
        for (let i = 0; i < mediafileLineItems.length; i++) {
          if (rowID === mediafileLineItems[i].ID) {
            mediafileLineItems[i] = row;
          }
        }
      }
      this.setState({
        mediafileLineItems,
      });
    };

    // for add mediafiles OR vast via new creative to selected line items
    // modal is accepted, update all line items, creates a new creative for the vast or
    //  creates associations for the global mediafile
    handleSelectionAcceptAddLineItems = () => {
      const selectedLineItems = this.state.mediafileLineItems.filter(
        (datum) => datum.tableData.checked,
      );

      // global mediafiles which are images are treated as banners for the native vast channel type
      const selectedImageGlobalMediafiles = this.state.data.filter((datum: IMediafileRowData) => {
        return datum.tableData.checked && isValidBannerMimetype(datum.MimeType);
      });
      const selectedVideoGlobalMediafiles = this.state.data.filter((datum: IMediafileRowData) => {
        return datum.tableData.checked && isValidVideoMimetype(datum.MimeType);
      });

      // find the selected mediafile ids, image and video handled differently since images are treated as banner
      const selectedLineItemIDs = selectedLineItems.map((datum) => datum.ID);
      const selectedImageGlobalMediafilesIDs = selectedImageGlobalMediafiles.map(
        (datum: IMediafileRowData) => datum.ID,
      );
      const selectedVideoGlobalMediafilesIDs = selectedVideoGlobalMediafiles.map(
        (datum: IMediafileRowData) => datum.ID,
      );

      if (this.isCurrentTabAsset()) {
        for (const lineItem of selectedLineItems) {
          for (const mediaFile of selectedImageGlobalMediafiles) {
            // forbid adding a banner onto a line item with two banner creatives
            if (
              hasPrimaryAndSecondaryBanner(lineItem) &&
              isValidBannerMimetype(mediaFile.MimeType)
            ) {
              this.setState({
                snackbarError: true,
                snackbarMessage: `The line item with ID ${lineItem.ID} already has two banner creatives. To add the mediafile with ID ${mediaFile.ID}, remove at least one banner creative from the line item.`,
              });
              return;
            }
          }
        }

        if (
          isEmpty(selectedLineItemIDs) ||
          (isEmpty(selectedImageGlobalMediafiles) && isEmpty(selectedVideoGlobalMediafiles))
        ) {
          this.setState({
            snackbarError: true,
            snackbarMessage:
              'Mediafiles and line items must be selected to create creatives with mediafiles.',
          });
          return;
        }

        this.setState(
          {
            updating: true,
            openAddCreativeDialog: false,
          },
          () => {
            let imageUploadPromise;
            let videoUploadPromise;
            const allPromises = [];
            if (!isEmpty(selectedImageGlobalMediafiles)) {
              imageUploadPromise = addGlobalMediafilesToLineItems({
                query: {
                  publisher_id: this.props.session.publisher.ID,
                  campaign_ids: selectedLineItemIDs.join(','),
                  global_mediafile_ids: selectedImageGlobalMediafilesIDs.join(','),
                  is_banner: 'true',
                },
              });
              allPromises.push(imageUploadPromise);
            }
            if (!isEmpty(selectedVideoGlobalMediafiles)) {
              videoUploadPromise = addGlobalMediafilesToLineItems({
                query: {
                  publisher_id: this.props.session.publisher.ID,
                  campaign_ids: selectedLineItemIDs.join(','),
                  global_mediafile_ids: selectedVideoGlobalMediafilesIDs.join(','),
                },
              });
              allPromises.push(videoUploadPromise);
            }
            Promise.all(allPromises)
              .then(() => {
                this.setState(
                  {
                    snackbarMessage: 'Mediafiles successfully added to line items.',
                    updating: false,
                  },
                  this.getGlobalMediaFilesAndLineItemsAndVasts,
                );
              })
              .catch(() => {
                this.setState({
                  snackbarMessage:
                    'Some mediafiles failed to be added to line items. Please try again',
                  snackbarError: true,
                  updating: false,
                });
                // some of the uploads might have succeeded so we need to refresh the list so the user can see
                this.getGlobalMediaFilesAndLineItemsAndVasts;
              });
          },
        );
      } else {
        // push checked line items
        for (const lineItem of this.state.vastLineItems) {
          if (lineItem.tableData.checked) {
            selectedLineItemIDs.push(lineItem.ID);
          }
        }
        // find checked vasts
        const globalVastIDs = Object.values(this.state.dataVast)
          .filter((datum: IVastRowData) => datum.tableData.checked)
          .map((datum: IVastRowData) => datum.ID)
          .join(',');

        if (selectedLineItemIDs.length === 0 || globalVastIDs.length === 0) {
          this.setState({
            snackbarError: true,
            snackbarMessage:
              'Vasts and line items must be selected to create creatives with mediafiles.',
          });
          return;
        }

        this.setState(
          {
            updating: true,
            openAddCreativeDialog: false,
          },
          () => {
            addGlobalVastToLineItems(
              selectedLineItemIDs.join(','),
              globalVastIDs,
              this.props.session.publisher.ID,
            )
              .then(() => {
                this.setState(
                  {
                    snackbarMessage: 'Vasts successfully added to line items.',
                    updating: false,
                  },
                  this.getGlobalMediaFilesAndLineItemsAndVasts,
                );
              })
              .catch(() => {
                this.setState({
                  snackbarMessage: 'Vasts failed to be added to line items. Please try again',
                  snackbarError: true,
                  updating: false,
                });
              });
          },
        );
      }
    };

    // for remove asset/vast from existing creatives modal
    // adds "selected" to the selectedCreatives in the modal
    handleSelectionChangeRemoveAssetVastFromCreatives = (
      rows: IAdserverCreativeWithCampaignInfoAndSelector[],
    ) => {
      const selectedCreatives = cloneDeep(this.state.selectedCreatives);
      const selectedCreativeIDs = [];
      const selectedCreativeNames = [];

      for (const row of rows) {
        const rowID = row.ID.toString();
        for (let i = 0; i < selectedCreatives.length; i++) {
          if (rowID === selectedCreatives[i].ID) {
            selectedCreatives[i] = row;
            selectedCreativeIDs.push(selectedCreatives[i].ID);
            selectedCreativeNames.push(selectedCreatives[i].Name);
          }
        }
      }
      this.setState({
        selectedCreatives,
        selectedCreativeIDs: selectedCreativeIDs.join(','),
        selectedCreativeNames: selectedCreativeNames.join(','),
      });
    };

    // for remove mediafiles from existing creatives modal
    // modal is accepted, open up dialog to check if they want to delete
    handleSelectionAcceptRemoveMediafileFromCreatives = () => {
      // need to make sure creatives being removed aren't primary banners on line items with both primary and secondary banners
      let selectedCreativeIDs = 0;
      const creativesByLineItemIDs: Record<string, IAdserverCreativeWithCampaignInfoAndSelector> =
        {};
      for (const c of this.state.selectedCreatives) {
        if (c.tableData.checked) {
          creativesByLineItemIDs[c.LineItemID] = c;
        }
      }
      for (const l of this.state.lineItems) {
        if (
          l.ID in creativesByLineItemIDs &&
          isPrimaryBannerForLineItemWithSecondaryBanner(creativesByLineItemIDs[l.ID], l)
        ) {
          this.setState({
            snackbarError: true,
            snackbarMessage: `Cannot delete creative with ID ${
              creativesByLineItemIDs[l.ID].ID
            } from line item with ID ${
              l.ID
            } since the creative is a primary banner and the line item has a secondary banner.`,
          });
          return;
        }
      }

      for (const creative of this.state.selectedCreatives) {
        if (creative.tableData.checked) {
          selectedCreativeIDs++;
        }
      }
      if (selectedCreativeIDs === 0) {
        this.setState({
          snackbarError: true,
          snackbarMessage: 'Creative(s) must be selected to remove creatives from mediafiles.',
        });
        return;
      }
      this.setState({
        openDeleteAssoModelDialog: true,
      });
    };

    // finds the media file or vast that was selected from the table and gets all campaigns associated
    // with the media file or vast to display in the modal
    handleOpenRemoveFromCreativeModal = (selectedID: string) => {
      let selected;
      if (this.isCurrentTabAsset()) {
        selected = this.state.mediafiles.find(
          (item: IAdserverGlobalMediafile) => item.ID === selectedID,
        );
      } else {
        selected = this.state.vasts.find(
          (item: IAdserverGlobalVast) => item.ID === parseInt(selectedID, 10),
        );
      }
      const selectedCreatives = [];
      const selectedName = (selected || { Name: '' }).Name;
      if (selected) {
        const selectedLineItems = [];
        for (const lineItem of this.state.lineItems) {
          if (
            selected.CampaignIDS &&
            selected.CampaignIDS.includes(parseInt(lineItem.ID, 10) || 0)
          ) {
            selectedLineItems.push(lineItem);
          }
        }

        // loop over selected line items to create the selected creatives and enrich with campaign ID and campaign name

        for (const lineItem of selectedLineItems) {
          for (const creative of lineItem.AdserverCreatives) {
            if (this.isCurrentTabAsset()) {
              // for mediafiles
              for (const id of creative.AdserverGlobalMediafileIDs) {
                if (id === selectedID) {
                  const enrichedCreative = {
                    ...creative,
                    LineItemID: lineItem.ID,
                    LineItemName: lineItem.Name,
                    AdserverOrderID: lineItem.AdserverOrderID.toString(),
                    tableData: { checked: false },
                  } as IAdserverCreativeWithCampaignInfoAndSelector;
                  selectedCreatives.push(enrichedCreative);
                }
              }
            } else {
              // for vast
              if (
                creative.AdserverGlobalVastID &&
                creative.AdserverGlobalVastID.toString() === selectedID
              ) {
                const enrichedCreative = {
                  ...creative,
                  LineItemID: lineItem.ID,
                  LineItemName: lineItem.Name,
                  AdserverOrderID: lineItem.AdserverOrderID.toString(),
                  tableData: { checked: false },
                } as IAdserverCreativeWithCampaignInfoAndSelector;
                selectedCreatives.push(enrichedCreative);
              }
            }
          }
        }
        this.setState(
          {
            selectedID,
            selectedName,
            selectedCreatives,
          },
          () => {
            this.setState({
              openCreativesDialog: true,
            });
          },
        );
      }

      return new Promise((resolve) => resolve(undefined));
    };

    // finds the media file that was selected from the table and gets all variants associated
    handleOpenVariantModal = (selectedID: string) => {
      const mediafile = this.state.mediafiles.find(
        (item: IAdserverGlobalMediafile) => item.ID === selectedID,
      );

      if (mediafile) {
        this.setState(
          {
            selectedID,
            selectedName: mediafile.Name,
          },
          () => {
            this.setState({
              openVariantsDialog: true,
            });
          },
        );
      }
      return new Promise((resolve) => resolve(undefined));
    };

    handleOpenCreateBannerModal = (name?: string, url?: string, advertiserId?: number) => {
      this.setState({
        openCreateBannerDialog: true,
        newCreateBannerURL: url || '',
        newCreateBannerName: name || '',
        BannerAdvertiserID: advertiserId || 0,
      });
    };

    handleOpenCreateOrEditModal = (
      name?: string,
      url?: string,
      advertiserId?: number,
      edit?: boolean,
    ) => {
      this.setState({
        openCreateDialog: true,
        newCreateURL: url || '',
        newCreateName: name || '',
        AdvertiserID: advertiserId || 0,
        edit: edit || false,
      });
    };

    // finds the variant and changes active
    handleChangeVariantActive = (variantID: number) => {
      const mediafiles = cloneDeep(this.state.mediafiles);
      const mediafile = mediafiles.find(
        (mf: IAdserverGlobalMediafile) => mf.ID === this.state.selectedID,
      );
      if (mediafile) {
        const variant = mediafile.VariantMediafiles.find(
          (mf: IVariantMediafile) => mf.ID === variantID,
        );
        if (variant) {
          variant.Active =
            variant.Active === EBool.EBool_TRUE ? EBool.EBool_FALSE : EBool.EBool_TRUE;
          this.setState(
            {
              updating: true,
            },
            () => {
              updateVariantMediafile({
                query: {
                  publisher_id: this.props.session.publisher.ID,
                  variant_mediafile_id: variantID.toString(),
                  active: variant.Active,
                },
              })
                .then(() => {
                  this.setState({
                    mediafiles,
                    snackbarMessage: 'Variant active successfully updated.',
                    updating: false,
                  });
                })
                .catch(() => {
                  this.setState({
                    snackbarMessage: 'Variant active failed to update.',
                    snackbarError: true,
                    updating: true,
                  });
                });
            },
          );
        }
      }
    };

    handleChangeSelectedResolutions = (_name: string, value: string[]) => {
      this.setState({
        selectedResolutions: value,
      });
    };

    // if closing the modal, we want to uncheck all the selections so its clear the next time its opened
    handleOpenAddCreativesModal = (open: boolean) => {
      const mediafileLineItems = cloneDeep(this.state.mediafileLineItems);
      if (!open) {
        for (const lineItem of mediafileLineItems) {
          lineItem.tableData.checked = false;
        }
      }
      this.setState({
        mediafileLineItems,
        openAddCreativeDialog: open,
      });
    };

    // deletes the association between a line item and media file or vast
    handleRemoveMediafileFromLineItem = () => {
      if (this.state.selectedID === '' || this.state.selectedCreativeIDs === '') {
        this.setState({
          snackbarMessage: 'Error deleting mediafile from line item. Please try again',
          snackbarError: true,
          openDeleteAssoModelDialog: false,
        });
        return new Promise((resolve) => resolve(undefined));
      }
      this.setState(
        {
          updating: true,
        },
        () => {
          if (this.isCurrentTabAsset()) {
            deleteGlobalMediaToCreativeAssociation({
              query: {
                publisher_id: this.props.session.publisher.ID,
                global_mediafile_id: this.state.selectedID,
                creative_ids: this.state.selectedCreativeIDs,
              },
            })
              .then(() => {
                this.setState(
                  {
                    updating: false,
                    selectedCreativeIDs: '',
                    selectedCreativeNames: '',
                    selectedID: '',
                    selectedName: '',
                    snackbarMessage: 'Successfully removed mediafile from line item.',
                    openDeleteAssoModelDialog: false,
                    openCreativesDialog: false,
                  },
                  this.getGlobalMediaFilesAndLineItemsAndVasts,
                );
              })
              .catch(() => {
                this.setState({
                  updating: false,
                  selectedCreativeIDs: '',
                  selectedCreativeNames: '',
                  selectedID: '',
                  selectedName: '',
                  snackbarMessage: 'Failed to remove mediafile from line item.',
                  snackbarError: true,
                  openDeleteAssoModelDialog: false,
                  openCreativesDialog: false,
                });
              });
          } else {
            deleteCreativesWithGlobalVastID(
              this.state.selectedCreativeIDs,
              this.state.selectedID,
              this.props.session.publisher.ID,
            )
              .then(() => {
                this.setState(
                  {
                    updating: false,
                    selectedCreativeIDs: '',
                    selectedCreativeNames: '',
                    selectedID: '',
                    selectedName: '',
                    snackbarMessage: 'Successfully removed Vast from line item.',
                    openDeleteAssoModelDialog: false,
                    openCreativesDialog: false,
                  },
                  this.getGlobalMediaFilesAndLineItemsAndVasts,
                );
              })
              .catch(() => {
                this.setState({
                  updating: false,
                  selectedCreativeIDs: '',
                  selectedCreativeNames: '',
                  selectedID: '',
                  selectedName: '',
                  snackbarMessage: 'Failed to remove Vast from line item.',
                  snackbarError: true,
                  openDeleteAssoModelDialog: false,
                  openCreativesDialog: false,
                });
              });
          }
        },
      );
      return new Promise((resolve) => resolve(undefined));
    };

    // closes the delete model
    handleCloseDeleteDialog = (): void => {
      this.setState({
        openDeleteAssoModelDialog: false,
      });
    };

    // Handles error generated in the list table component.
    handleListTableError(errorMsg: string) {
      this.setState({
        snackbarError: true,
        snackbarMessage: errorMsg,
      });
    }

    uploadCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { target } = event;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        const filenameParams = file.name.split('.');
        const ext = filenameParams[filenameParams.length - 1];
        if (ext.toLowerCase() !== 'csv') {
          this.setState({
            snackbarMessage: 'Only CSV files are supported',
            snackbarError: true,
          });
          return;
        }

        uploadVastCSVfile({
          publisher_id: this.props.session.publisher.ID,
          name: filenameParams[0],
          file,
        })
          .then((json: any) => {
            if (json.response && json.response.errors) {
              this.setState({
                snackbarMessage: `CSV Upload failed: ${json.response.errors}`,
                snackbarError: true,
              });
            } else {
              this.setState(
                {
                  snackbarMessage: 'CSV Upload successful',
                },
                this.getGlobalMediaFilesAndLineItemsAndVasts,
              );
            }
          })
          .catch(() => {
            this.setState({
              snackbarMessage: 'CSV Upload failed',
              snackbarError: true,
            });
          });
      }
      // reset the value of the event so the same file name can be uploaded
      event.target.value = '';
    };

    // renders the custom tool bar for the add creatives to line item modal
    renderAddCreativesModalToolbar = (props: any) => {
      const selectedLineItemCount = (
        this.isCurrentTabAsset() ? this.state.mediafileLineItems : this.state.vastLineItems
      ).filter((c: IAdserverCampaignWithSelector) => c.tableData.checked).length;
      return (
        // weird margins to make the search box align
        <Grid item style={{ marginLeft: -10 }}>
          <Grid container direction='row' justify='space-between'>
            <Typography style={{ fontSize: 24, fontWeight: 500, marginLeft: 22 }}>
              {this.isCurrentTabAsset() ? 'Add Asset to Line Items' : 'Add Vast to Line Items'}
            </Typography>
            <CloseIcon
              style={{ cursor: 'pointer', color: '#707070' }}
              onClick={this.handleCloseAddCreativeDialog}
            />
          </Grid>
          <Typography style={{ fontSize: 14, marginLeft: 22 }}>
            {`Select line items to link to the selected ${
              this.isCurrentTabAsset() ? 'asset' : 'Vast'
            } by creating a new creative on each line item`}
          </Typography>
          <MTableToolbar {...props} />
          <Grid
            container
            direction='row'
            style={{ cursor: 'pointer' }}
            onClick={this.handleSelectionAcceptAddLineItems}
          >
            <LinkIcon style={{ color: '#5D66FA', fontWeight: 500, marginLeft: 22 }} />
            <Typography style={{ color: '#5D66FA', fontWeight: 500, marginLeft: 10 }}>
              {`Add ${
                this.isCurrentTabAsset() ? 'Asset' : 'Vast'
              } to ${selectedLineItemCount} Line Items`}
            </Typography>
          </Grid>
        </Grid>
      );
    };

    // renders the custom tool bar for the variants modal
    renderVariantsModalToolbar = (props: any) => {
      return (
        // weird margins to make the search box align
        <Grid item style={{ marginLeft: -10 }}>
          <Grid container direction='column' justify='space-between'>
            <Typography style={{ fontSize: 24, fontWeight: 500, marginLeft: 22 }}>
              {`Variants of ${this.state.selectedName}`}
            </Typography>
            <Typography style={{ fontSize: 14, marginLeft: 22 }}>
              {`Activate / deactive variants of the original selected asset`}
            </Typography>
          </Grid>
          <MTableToolbar {...props} />
        </Grid>
      );
    };

    // renders the custom tool bar for the line item modal
    renderCreativesDialog = (props: any) => {
      const { currentTab } = this.state;
      const selectedCreativesCount = this.state.selectedCreatives.filter(
        (mf: IAdserverCreativeWithCampaignInfoAndSelector) => mf.tableData.checked,
      ).length;
      return (
        // weird margins to make the search box align
        <Grid item style={{ marginLeft: -10 }}>
          <Grid container direction='row' justify='space-between'>
            <Grid item xs={11}>
              <Typography style={{ fontSize: 24, fontWeight: 500, marginLeft: 22 }}>
                {`Creatives Linked to ${currentTab} [${this.state.selectedName}]`}
              </Typography>
              <Typography style={{ fontSize: 14, marginLeft: 22 }}>
                {this.isCurrentTabAsset()
                  ? `Remove asset from the selected creatives and then delete creatives that no longer have any assets assigned.  The selected asset will remain available in the library.`
                  : `Delete selected creatives using the selected VAST.  The selected VAST will remain available in the library.`}
              </Typography>
            </Grid>
            <Grid item xs={1} style={{ textAlign: 'right' }}>
              <CloseIcon
                style={{ cursor: 'pointer', color: '#707070' }}
                onClick={this.handleCloseCreativeDialog}
              />
            </Grid>
          </Grid>
          <MTableToolbar {...props} />
          <Grid
            container
            direction='row'
            style={{ cursor: 'pointer' }}
            onClick={this.handleSelectionAcceptRemoveMediafileFromCreatives}
          >
            <LinkOffIcon style={{ color: '#5D66FA', fontWeight: 500, marginLeft: 22 }} />
            <Typography style={{ color: '#5D66FA', fontWeight: 500, marginLeft: 10 }}>
              {selectedCreativesCount === 1
                ? `Remove ${currentTab} from Selected Line Item`
                : `Remove ${currentTab} From ${selectedCreativesCount} Line Items`}
            </Typography>
          </Grid>
        </Grid>
      );
    };

    // renders the custom header for the main list
    // remove the select all checkbox
    renderMainListHeader = (props: any) => {
      const newProps = cloneDeep(props);
      newProps.showSelectAllCheckbox = false;
      return <MTableHeader {...newProps} />;
    };

    renderMenuTitle = () => {
      const { classes } = this.props;
      return (
        <Grid item>
          <Button className={classes.button} style={{ width: 135 }}>
            <Grid container direction='row' alignItems='center' spacing={1}>
              <Grid item>
                <AddIcon
                  style={{
                    marginTop: 5,
                    color: getColorTheme('primaryButtons'), // Color for upload button of Campaign Settings
                  }}
                />
              </Grid>
              <Grid item>
                <Typography className={this.props.classes.menuButtonText}>
                  {this.isCurrentTabAsset() ? 'ADD ASSET' : 'ADD VAST'}
                </Typography>
              </Grid>
            </Grid>
          </Button>
        </Grid>
      );
    };

    renderUploadRow = () => {
      const { classes } = this.props;
      return (
        <Grid container direction='column'>
          <Grid item>
            <Grid container direction='row' alignItems='center'>
              <Grid item>
                <label>
                  <Grid
                    container
                    direction='row'
                    alignItems='center'
                    spacing={2}
                    style={{ cursor: 'pointer' }}
                  >
                    <Grid item>
                      <BackupIcon
                        style={{
                          color: '#4952FF',
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <Typography className={classes.menuButtonTextBlue}>
                        UPLOAD MEDIAFILE(S)
                      </Typography>
                    </Grid>
                  </Grid>
                  <input
                    multiple
                    type='file'
                    style={{ display: 'none', marginLeft: '20px' }}
                    onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                      this.handleUploadMediafiles(evt);
                    }}
                  />
                </label>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <CategoryMultiSelect
              key='selectedResolutions'
              helperText='Select variant resolutions to be created'
              name='selectedResolutions'
              disabled={!this.state.writeAuthorization.isAuthorized}
              items={ResolutionTypes}
              onChange={this.handleChangeSelectedResolutions}
              values={this.state.selectedResolutions}
              style={{ width: 225, zIndex: 999 }}
              textfieldClass={this.props.classes.insideAccordionTextfield}
              selectFontSize={14}
              disableClear={true}
            />
          </Grid>
        </Grid>
      );
    };

    renderCreateBannerFromAssetURLRow = () => {
      return CreateBannerFromAssetURLRow(this.handleOpenCreateBannerModal);
    };

    renderCreateBannerFromDirectUploadRow = () => {
      return CreateBannerFromDirectUploadRow(this.handleUploadMediafiles);
    };

    renderCreateFromAssetURLRow = () => {
      return (
        <Grid item>
          <Grid
            container
            direction='row'
            alignItems='center'
            spacing={2}
            onClick={() => this.handleOpenCreateOrEditModal()}
          >
            <Grid item>
              <NoteAddIcon
                style={{
                  color: '#4952FF',
                }}
              />
            </Grid>
            <Grid item>
              <Typography className={this.props.classes.menuButtonTextBlue}>
                {this.isCurrentTabAsset() ? 'ADD EXT HOSTED FILE' : 'CREATE FROM URL'}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderDownloadCreateVastCSV = () => {
      return (
        <Grid item>
          <Grid container direction='row' alignItems='center' spacing={2}>
            <Grid item>
              <GetAppIcon
                style={{
                  color: '#4952FF',
                }}
              />
            </Grid>
            <Grid item>
              <a
                target='_blank'
                rel='noopener'
                style={{ textDecoration: 'none' }}
                download
                href={genDownloadVastCSVURL(this.props.session.publisher.ID, true)}
              >
                <Typography className={this.props.classes.menuButtonTextBlue}>
                  DOWNLOAD CREATE CSV
                </Typography>
              </a>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderDownloadUpdateVastCSV = () => {
      return (
        <Grid item>
          <Grid container direction='row' alignItems='center' spacing={2}>
            <Grid item>
              <GetAppIcon
                style={{
                  color: '#4952FF',
                }}
              />
            </Grid>
            <Grid item>
              <a
                target='_blank'
                rel='noopener'
                style={{ textDecoration: 'none' }}
                href={genDownloadVastCSVURL(this.props.session.publisher.ID, false)}
              >
                <Typography className={this.props.classes.menuButtonTextBlue}>
                  DOWNLOAD UPDATE CSV
                </Typography>
              </a>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderUploadVastCSV = () => {
      return (
        <label>
          <Grid item>
            <Grid
              container
              direction='row'
              alignItems='center'
              spacing={2}
              style={{ cursor: 'pointer' }}
            >
              <Grid item>
                <PublishIcon
                  style={{
                    color: '#4952FF',
                  }}
                ></PublishIcon>
              </Grid>
              <Grid item>
                <Typography className={this.props.classes.menuButtonTextBlue}>
                  UPLOAD VAST CSV
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <input type='file' style={{ display: 'none' }} accept='.csv' onChange={this.uploadCSV} />
        </label>
      );
    };

    // renders the dropdown button to add vast or asset
    renderAddButton = () => {
      const createFromAssetURLRow = {
        closeOnClick: true,
        render: this.renderCreateFromAssetURLRow,
      };
      const bannerFromAssetURLRow = {
        closeOnClick: true,
        render: this.renderCreateBannerFromAssetURLRow,
      };
      const bannerFromDirectUploadRow = {
        closeOnClick: true,
        render: this.renderCreateBannerFromDirectUploadRow,
      };
      const uploadRow = {
        closeOnClick: false,
        render: this.renderUploadRow,
      };
      const downloadCreateVastCSV = {
        closeOnClick: true,
        render: this.renderDownloadCreateVastCSV,
      };
      const downloadUpdateVastCSV = {
        closeOnClick: true,
        render: this.renderDownloadUpdateVastCSV,
      };
      const uploadNewVastCSV = {
        closeOnClick: true,
        render: this.renderUploadVastCSV,
      };
      let menuButtonItems = [];
      if (this.isNativeVastAuthorized) {
        menuButtonItems.push(bannerFromAssetURLRow, bannerFromDirectUploadRow);
      }
      menuButtonItems.push(createFromAssetURLRow, uploadRow);
      if (!this.isCurrentTabAsset()) {
        menuButtonItems = [
          createFromAssetURLRow,
          downloadCreateVastCSV,
          downloadUpdateVastCSV,
          uploadNewVastCSV,
        ];
      }
      return (
        <Grid
          onClick={() => {
            this.setState({
              forceMenuButtonClosed: false,
            });
          }}
        >
          <MenuButton
            forceClosed={this.state.forceMenuButtonClosed}
            titleRender={this.renderMenuTitle}
            items={menuButtonItems}
            minWidth={250}
            marginTop={60}
            marginLeft={-95}
            enableDividers={true}
          />
        </Grid>
      );
    };

    // renders the custom tool bar for the asset or vast list
    renderListToolbar = (props: any) => {
      const { classes } = this.props;
      const { data, dataVast } = this.state;

      // count the number of selected mediafiles or vast
      let selectedCount = 0;
      if (this.isCurrentTabAsset()) {
        const clonedData = cloneDeep(data);
        for (const datum of clonedData) {
          if (datum.tableData.checked) {
            selectedCount++;
          }
        }
      } else {
        const clonedData = cloneDeep(dataVast);
        for (const id in clonedData) {
          if (clonedData[id].tableData.checked) {
            selectedCount++;
          }
        }
      }

      const copyProps = {
        ...props,
        searchFieldStyle: { ...props.searchFieldStyle, marginLeft: '-48px', width: '300px' },
      };

      return (
        <Grid item>
          <Grid container>
            <Cols
              paddingTop={spacing.medium}
              style={{
                width: '100%',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {/* search bar - grid with MTableToolbarWrapper class is required for auto focusing search upon loading the list table component */}
              <Col paddingLeft='8px'>
                <Grid item className='MTableToolbarWrapper'>
                  <MTableToolbar {...copyProps} />
                </Grid>
              </Col>
              {/* Buttons */}
              <Grid item>{this.renderAddButton()}</Grid>
            </Cols>
            {/* Add Mediafiles / Vast to Line Items Button and remove button*/}
            <Grid item className={classes.toolbarRow} style={{ marginLeft: 13, marginBottom: 10 }}>
              <Grid container direction='row' alignItems='center' spacing={3}>
                {selectedCount === 0 && (
                  <Grid item>
                    <Typography>
                      {this.isCurrentTabAsset()
                        ? 'Select asset to manage association to line items'
                        : 'Select VAST to manage association to line items'}
                    </Typography>
                  </Grid>
                )}
                {selectedCount !== 0 && (
                  <Grid item>
                    <Grid
                      container
                      direction='row'
                      justifyContent='space-between'
                      alignItems='center'
                      style={{ cursor: 'pointer ' }}
                      onClick={() => {
                        if (selectedCount > 0) {
                          this.handleOpenAddCreativesModal(true);
                        }
                      }}
                    >
                      <Grid container direction='row'>
                        <LinkIcon
                          style={{
                            color: selectedCount === 1 ? '#5D66FA' : '#707070',
                            fontWeight: 500,
                          }}
                        />
                        <Typography
                          style={{
                            color: selectedCount > 0 ? '#5D66FA' : '#707070',
                            fontWeight: 500,
                            marginLeft: 10,
                          }}
                        >
                          Add to Line Items
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                {selectedCount !== 0 && (
                  <Grid item>
                    <Grid
                      container
                      direction='row'
                      justify='space-between'
                      alignItems='center'
                      spacing={1}
                      style={{ cursor: 'pointer ' }}
                      onClick={() => {
                        let selectedID = '';
                        if (this.isCurrentTabAsset()) {
                          selectedID = (
                            this.state.data.find(
                              (row: IMediafileRowData) => row.tableData.checked,
                            ) || { ID: '' }
                          ).ID.toString();
                        } else {
                          for (const id in this.state.dataVast) {
                            if (this.state.dataVast[id].tableData.checked) {
                              selectedID = id.toString();
                              break;
                            }
                          }
                        }
                        this.handleOpenRemoveFromCreativeModal(selectedID);
                      }}
                    >
                      <Grid container direction='row'>
                        <LinkOffIcon
                          style={{ color: '#5D66FA', fontWeight: 500, marginLeft: 22 }}
                        />
                        <Typography style={{ color: '#5D66FA', fontWeight: 500, marginLeft: 10 }}>
                          Remove from Line Items
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderAssetsList = () => {
      // NOTE: We destructure props and states used in render functions to clearly
      // show what we are passing as well as keeping the code clean and easier to read
      const { mediafiles, loading, updating, data, editableName, editableID } = this.state;
      if (mediafiles.length === 0 && loading) {
        return <LoadingView active={true} text='Loading the Creatives Library' />;
      }

      // column information for material table => 'field' should match name in state,
      // 'title' for column display in table, 'renderTypes' can be found in list-table.ts. If the 'renderType' doesn't exist
      // there yet, create a new 'renderType' if it can be reused or if it seems unique, set a custom 'render'
      // in the 'render' field.  Additional column props can be found at https://material-table.com/#/docs/get-started
      const columns: IListTableColumn[] = [
        { field: 'Name', title: 'Asset Name', renderType: 'normal', width: '30%' },
        { field: 'URL', title: 'Play', renderType: 'play', width: '10%', searchable: false },
        { field: 'Advertiser', title: 'Advertiser', renderType: 'normal', width: '10%' },
        {
          field: 'Creatives',
          title: 'Associations',
          renderType: 'modal',
          width: '10%',
          searchable: false,
        },
        {
          field: 'Variants',
          title: 'Variants',
          renderType: 'modal2',
          width: '10%',
          searchable: false,
        },
        { field: 'Duration', title: 'Duration', renderType: 'normal', width: '10%' },
        { field: 'Bitrate', title: 'Bitrate', renderType: 'normal', width: '10%' },
        { field: 'AspectRatio', title: 'Aspect Ratio', renderType: 'normal', width: '10%' },
        { field: 'Dimensions', title: 'Dimensions', renderType: 'normal', width: '10%' },
        {
          field: 'Actions',
          title: 'Actions',
          renderType: 'actions',
          searchable: false,
          width: '10%',
        },
      ];
      // loop over data to switch the name of the edited row to the temp name
      const clonedData = cloneDeep(data);
      for (const datum of clonedData) {
        if (datum.ID === editableID) {
          datum.Name = editableName;
          break;
        }
      }

      return (
        <Grid item style={{ marginTop: 20 }}>
          <ListTable
            title={''}
            columns={columns}
            data={clonedData}
            isLoading={loading}
            isUpdating={updating}
            handleSetUpdating={this.handleSetUpdating}
            handleOpenModal={this.handleOpenRemoveFromCreativeModal}
            handleOpenModal2={this.handleOpenVariantModal}
            handleOpenModal3={(assetId: number) => {
              this.setState(
                {
                  selectedID: assetId.toString(),
                },
                () => {
                  const mediafile = mediafiles.find(
                    (mf: IAdserverGlobalMediafile) => mf.ID === assetId.toString(),
                  );
                  const name = mediafile ? mediafile.Name : '';
                  const url = mediafile ? mediafile.URL : '';
                  const advertiserId = mediafile ? mediafile.AdvertiserID : 0;
                  this.handleOpenCreateOrEditModal(name, url, advertiserId, true);
                },
              );
            }}
            selectionEnabled={true}
            handleSelectionChange={this.handleSelectionChange}
            handleDeleteItem={this.handleDeleteItem}
            fixedEnabled={true}
            disableDeleteMsg='Mediafile cannot be deleted while assigned to line items.'
            renderCustomToolbar={this.renderListToolbar}
            renderCustomHeader={this.renderMainListHeader}
            pageSize={10}
            readRoleName={'read_campaign'}
            writeRoleName={'write_campaign'}
            disableUpdateFocus={true}
            enableSearchBarLeft
          />
        </Grid>
      );
    };

    renderVastList = () => {
      const { loading, updating, dataVast, vasts } = this.state;
      if (vasts.length === 0 && loading) {
        return <LoadingView active={true} text='Loading the Creatives Library' />;
      }

      // column information for material table => 'field' should match name in state,
      // 'title' for column display in table, 'renderTypes' can be found in list-table.ts. If the 'renderType' doesn't exist
      // there yet, create a new 'renderType' if it can be reused or if it seems unique, set a custom 'render'
      // in the 'render' field.  Additional column props can be found at https://material-table.com/#/docs/get-started
      const columns: IListTableColumn[] = [
        { field: 'Name', title: 'VAST Name', renderType: 'normal', width: '30%' },
        { field: 'URL', title: 'Play', renderType: 'vastPlay', width: '10%', searchable: false },
        { field: 'Advertiser', title: 'Advertiser', renderType: 'normal', width: '10%' },
        { field: 'URL', title: 'URL', renderType: 'normal', width: '40%' },
        {
          field: 'UpdatedAt',
          title: 'Updated At',
          renderType: 'date',
          width: '10%',
          customSort: this.compareUpdatedAtDates,
        },
        {
          field: 'AdserverCreatives',
          title: 'Associations',
          renderType: 'modal',
          width: '10%',
          searchable: false,
        },
        {
          field: 'Actions',
          title: 'Actions',
          renderType: 'actions',
          searchable: false,
          width: '10%',
        },
      ];
      // loop over data to switch the name of the edited row to the temp name
      const clonedData = cloneDeep(dataVast);
      return (
        <Grid item style={{ marginTop: 20 }}>
          <ListTable
            title={''}
            columns={columns}
            data={Object.values(clonedData)}
            isLoading={loading}
            isUpdating={updating}
            handleSetUpdating={this.handleSetUpdating}
            handleOpenModal={this.handleOpenRemoveFromCreativeModal}
            handleOpenModal3={(vastId: number) => {
              this.setState(
                {
                  selectedID: vastId.toString(),
                },
                () => {
                  const vast = this.state.vasts.find(
                    (vast: IAdserverGlobalVast) => vast.ID === Number(vastId),
                  );
                  const name = vast ? vast.Name : '';
                  const url = vast ? vast.URL : '';
                  const advertiserId = vast ? vast.AdvertiserID : 0;
                  this.handleOpenCreateOrEditModal(name, url, advertiserId, true);
                },
              );
            }}
            selectionEnabled={true}
            handleSelectionChange={this.handleSelectionChange}
            handleDeleteItem={this.handleDeleteItem}
            fixedEnabled={true}
            disableDeleteMsg='VAST cannot be deleted while assigned to line items.'
            renderCustomToolbar={this.renderListToolbar}
            renderCustomHeader={this.renderMainListHeader}
            pageSize={5}
            readRoleName={'read_campaign'}
            writeRoleName={'write_campaign'}
            handleError={(errorMsg: string) => this.handleListTableError(errorMsg)}
            secondTabSelection={true}
            disableUpdateFocus={true}
            enableSearchBarLeft
          />
        </Grid>
      );
    };

    setSettingsTabsCurrentTab = (val: number) => {
      this.setState({
        currentTab: val === 0 ? 'Asset' : 'Vast',
      });
    };

    // define tabs and corresponding render functions
    renderTabs(): ReactElement {
      const tabs: ITab[] = [
        {
          label: 'Asset',
          render: this.renderAssetsList,
        },
        {
          label: 'Vast',
          render: this.renderVastList,
        },
      ];
      return (
        <SettingsTabs
          tabs={tabs}
          setSettingsTabsCurrentTab={this.setSettingsTabsCurrentTab}
          marginTop={10}
        />
      );
    }

    render() {
      // NOTE: We destructure props and states used in render functions to clearly
      // show what we are passing as well as keeping the code clean and easier to read
      const {
        mediafiles,
        loading,
        snackbarError,
        snackbarMessage,
        readAuthorization,
        writeAuthorization,
        selectedCreatives,
        openCreativesDialog,
        mediafileLineItems,
        openAddCreativeDialog,
        selectedID,
        openVariantsDialog,
        openCreateDialog,
        newCreateName,
        newCreateURL,
        currentTab,
        vastLineItems,
      } = this.state;
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      if (mediafiles.length === 0 && loading) {
        return <LoadingView active={true} text='Loading the Creatives Library' />;
      }

      if (!readAuthorization.isAuthorized && !writeAuthorization.isAuthorized) {
        return (
          <ModelMissingPermission
            missingPermissions={[
              ...readAuthorization.missingPermissions,
              ...writeAuthorization.missingPermissions,
            ]}
          />
        );
      }

      // line item name custom column for  creatives modal
      const lineItemNameModalColumn = {
        title: 'Line Item Name',
        field: 'LineItemName',
        searchable: true,
        render: (rowData: IAdserverCreativeWithCampaignInfoAndSelector) => {
          return (
            <Button
              style={{ marginRight: 25 }}
              onClick={() => {
                navigate(RouteCampaignExisting, extractRouteParams(this.props), {
                  campaignID: rowData.LineItemID,
                  order: rowData.AdserverOrderID.toString(),
                });
              }}
            >
              <Grid container direction='column' justify='center' alignItems='flex-start'>
                <Typography style={{ fontSize: 14, fontWeight: 500 }}>
                  {rowData.LineItemName}
                </Typography>
              </Grid>
            </Button>
          );
        },
      };

      // line item id custom column for  creatives modal
      const lineItemIDModalColumn = {
        title: 'Line Item ID',
        field: 'LineItemID',
        searchable: true,
        render: (rowData: IAdserverCreativeWithCampaignInfoAndSelector) => {
          return (
            <Button
              style={{ marginRight: 25 }}
              onClick={() => {
                navigate(RouteCampaignExisting, extractRouteParams(this.props), {
                  campaignID: rowData.LineItemID,
                  order: rowData.AdserverOrderID.toString(),
                });
              }}
            >
              <Grid container direction='column' justify='center' alignItems='flex-start'>
                <Typography style={{ fontSize: 14, fontWeight: 500 }}>
                  {rowData.LineItemID}
                </Typography>
              </Grid>
            </Button>
          );
        },
      };

      // grab the variants for the selected mediafile
      let variants = [] as IVariantMediafile[];
      const mediafile = this.state.mediafiles.find(
        (item: IAdserverGlobalMediafile) => item.ID === selectedID,
      );
      if (mediafile) {
        variants = mediafile.VariantMediafiles;
      }

      // play column for variant modal
      const variantPlayColumn = {
        title: 'Play',
        field: 'Play',
        searchable: true,
        render: (rowData: IVariantMediafile) => {
          return (
            <a
              href={rowData.URL}
              style={{
                color: 'black',
                marginRight: 22,
              }}
              target='_blank'
            >
              <PlayCircleFilledIcon />
            </a>
          );
        },
      };

      // active column for variant modal
      const variantActiveColumn = {
        title: 'Active',
        field: 'Active',
        searchable: true,
        render: (rowData: IVariantMediafile) => {
          return (
            <Switch
              className={this.props.classes.switch}
              checked={rowData.Active === EBool.EBool_TRUE}
              name='Active'
              onChange={() => this.handleChangeVariantActive(rowData.ID)}
              color='primary'
              style={{ marginRight: 25 }}
            />
          );
        },
      };

      // bitrate column for variant modal
      const variantBitrateColumn = {
        title: 'Bitrate',
        field: 'Bitrate',
        searchable: true,
        render: (rowData: IVariantMediafile) => {
          let Bitrate = rowData.Bitrate.toString();
          if (rowData.Bitrate > 10000) {
            Bitrate = Math.floor(rowData.Bitrate / 1000).toString() + ' kbps';
          }
          return (
            <Grid>
              <Typography style={{ fontSize: 14 }}>{Bitrate}</Typography>
            </Grid>
          );
        },
      };

      // resolution column for variant modal
      const variantResolutionColumn = {
        title: 'Resolution',
        field: 'Resolution',
        searchable: true,
        render: (rowData: IVariantMediafile) => {
          return (
            <Grid>
              <Typography style={{ fontSize: 14 }}>
                {`${rowData.Width} x ${rowData.Height}`}
              </Typography>
            </Grid>
          );
        },
      };

      // custom column for  global vast to line items modal
      const globalVastToCreativesModalColumn = {
        title: 'Current Vast',
        field: 'Vast',
        render: (rowData: IAdserverCampaignWithSelector) => {
          const allGlobalVasts = [] as IAdserverGlobalVast[];
          for (const vast of this.state.vasts) {
            if (vast.CampaignIDS && vast.CampaignIDS.includes(parseInt(rowData.ID, 10))) {
              allGlobalVasts.push(vast);
            }
          }
          let menuItems = [
            <MenuItem key={rowData.ID} value={rowData.Name}>
              No Vasts
            </MenuItem>,
          ];
          if (allGlobalVasts.length) {
            menuItems = allGlobalVasts.map((vast: IAdserverGlobalVast) => {
              return (
                <MenuItem key={vast.ID} value={vast.Name}>
                  {vast.Name}
                </MenuItem>
              );
            });
          }
          return (
            <TextField
              fullWidth
              select={true}
              label={`${allGlobalVasts.length} Vast`}
              margin='normal'
              style={{ width: '200px', marginBottom: 10 }}
            >
              {menuItems}
            </TextField>
          );
        },
      };
      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largePadding>
            {/* list table */}
            {this.renderTabs()}
            {/* snackbar for progress */}
            <Snackbar
              open={true}
              anchorOrigin={{
                horizontal: 'right',
                vertical: 'bottom',
              }}
            >
              <ModelSnackBarProgressBarContainer
                uploadManager={this.uploadManager}
                isSavable={false}
                isMediafile={true}
              />
            </Snackbar>
            {/* snackbar error msgs */}
            <SnackbarView
              autoHideDuration={30000}
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.handleCloseSnackbar}
            >
              {snackbarMessage}
            </SnackbarView>
            {/* remove from line items dialog  */}
            <Dialog
              fullWidth
              maxWidth='md'
              keepMounted
              open={openCreativesDialog}
              onClose={this.handleCloseCreativeDialog}
            >
              <ModalSelectTable
                title=''
                items={selectedCreatives}
                renderCustomToolbar={this.renderCreativesDialog}
                enableSearchBarLeft={true}
                customColumns={[lineItemNameModalColumn, lineItemIDModalColumn]}
                interactive={false}
                enableMultiSelect={true}
                handleSelectionChange={this.handleSelectionChangeRemoveAssetVastFromCreatives}
                nameColumnRename={'Creative Name'}
              />
            </Dialog>
            {/* add to line item dialog */}
            <Dialog
              fullWidth
              maxWidth='md'
              keepMounted
              open={openAddCreativeDialog}
              onClose={this.handleCloseAddCreativeDialog}
            >
              {this.isCurrentTabAsset() && (
                <ModalSelectTable
                  title=''
                  items={mediafileLineItems}
                  interactive={false}
                  enableMultiSelect={true}
                  handleSelectionChange={this.handleSelectionChangeAddLineItems}
                  handleSelectionAccept={this.handleSelectionAcceptAddLineItems}
                  enableSearchBarLeft={true}
                  renderCustomToolbar={this.renderAddCreativesModalToolbar}
                  customColumns={[globalMediafileToCreativesModalColumn]}
                  nameColumnRename={'Line Item Name'}
                />
              )}
              {currentTab === 'Vast' && (
                <ModalSelectTable
                  title=''
                  items={vastLineItems}
                  interactive={false}
                  enableMultiSelect={true}
                  handleSelectionChange={this.handleSelectionChangeAddLineItems}
                  handleSelectionAccept={this.handleSelectionAcceptAddLineItems}
                  enableSearchBarLeft={true}
                  renderCustomToolbar={this.renderAddCreativesModalToolbar}
                  customColumns={[globalVastToCreativesModalColumn]}
                  nameColumnRename={'Line Item Name'}
                />
              )}
            </Dialog>
            {/* variants dialog */}
            <Dialog
              fullWidth
              maxWidth='md'
              keepMounted
              open={openVariantsDialog}
              onClose={this.handleCloseVariantDialog}
            >
              <ModalSelectTable
                title=''
                items={variants}
                interactive={false}
                enableSearchBarLeft={true}
                renderCustomToolbar={this.renderVariantsModalToolbar}
                customColumns={[
                  variantPlayColumn,
                  variantActiveColumn,
                  variantBitrateColumn,
                  variantResolutionColumn,
                ]}
                nameColumnRename={'Variant Name'}
              />
            </Dialog>
            {/* create mediafile from url dialog */}
            <Dialog
              fullWidth
              maxWidth='md'
              keepMounted
              open={openCreateDialog}
              onClose={this.handleCloseCreateMediafileModal}
            >
              <CreateOrEditFromURLModal
                newCreateName={newCreateName}
                newCreateURL={newCreateURL}
                handleChange={this.handleChange}
                handleCreateFromURL={this.handleCreateFromURL}
                handleUpdateFromURL={this.handleUpdateFromURL}
                buttonClass={this.props.classes.button}
                isMediafile={this.isCurrentTabAsset()}
                publisher={this.props.session.publisher}
                edit={this.state.edit}
                handleChangeAdvertiser={this.handleChangeAdvertiser}
                advertiserID={this.state.AdvertiserID}
                user={this.props.user}
              />
            </Dialog>
            {/* create banner from url dialog */}
            <Dialog
              fullWidth
              maxWidth='md'
              keepMounted
              open={this.state.openCreateBannerDialog}
              onClose={() =>
                this.setState({
                  openCreateBannerDialog: false,
                })
              }
            >
              <CreateBannerModal
                newCreateBannerName={this.state.newCreateBannerName}
                newCreateBannerURL={this.state.newCreateBannerURL}
                handleChangeBanner={this.handleChangeBanner}
                handleCreateBannerFromURL={this.handleCreateBannerFromURL}
                buttonClass={this.props.classes.button}
                publisher={this.props.session.publisher}
                handleChangeBannerAdvertiser={this.handleChangeAdvertiser}
                advertiserID={this.state.AdvertiserID}
                user={this.props.user}
              />
            </Dialog>
            {/* modal dialog for deleting mediafile to creative association */}
            <ModelDialogDeleteView
              active={this.state.openDeleteAssoModelDialog}
              model='Association'
              ID={this.state.selectedID}
              Name={`${this.state.selectedName} to ${this.state.selectedCreativeNames}`}
              deleteModel={this.handleRemoveMediafileFromLineItem}
              onDelete={this.handleCloseDeleteDialog}
              onError={this.handleCloseDeleteDialog}
              onClose={this.handleCloseDeleteDialog}
              disableSnackbar={true}
            />
          </Panel>
        </Panels>
      );
    }
  },
);
